import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { NuqsAdapter } from "nuqs/adapters/react-router/v6";
import { Toaster } from "sonner";

import App from "@/App";
import { ThemeProvider } from "@/components/theme-provider";
import { AuthProvider } from "@/contexts/AuthContext";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";
import { DensityProvider } from "@/contexts/DensityContext";
import { ShortcutsCheatsheetProvider } from "@/components/shared/ShortcutsCheatsheet";
import { ErrorBoundary } from "@/components/error/ErrorBoundary";
import "@/styles/globals.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        const status = (error as { status?: number })?.status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 2;
      },
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Provider-Reihenfolge (aussen → innen):
 *   StrictMode
 *     ThemeProvider           — next-themes (paypilot.theme), setzt class="dark" auf <html>
 *       QueryClientProvider   — TanStack Query (Server-State)
 *         ErrorBoundary       — Globaler Recovery-Fallback (statt weisser Seite)
 *           BrowserRouter     — React Router v6 (history für NuqsAdapter)
 *             NuqsAdapter     — nuqs auf react-router v6 (URL-State; Pages: useQueryState)
 *               AuthProvider  — OIDC / kalkuel_auth-Cookie
 *                 WorkspaceProvider — aktive Workspace-Auswahl, Hub-Roundtrip
 *                   DensityProvider — Tabellen-Density (compact/cozy/relaxed)
 *                     ShortcutsCheatsheetProvider — globaler `?`-Shortcut + Modal
 *                       <App />
 */
ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary scope="root">
          <BrowserRouter>
            <NuqsAdapter>
              <AuthProvider>
                <WorkspaceProvider>
                  <DensityProvider>
                    <ShortcutsCheatsheetProvider>
                      <App />
                    </ShortcutsCheatsheetProvider>
                  </DensityProvider>
                </WorkspaceProvider>
              </AuthProvider>
            </NuqsAdapter>
          </BrowserRouter>
        </ErrorBoundary>
        <Toaster
          position="top-right"
          richColors
          closeButton
          toastOptions={{
            classNames: {
              toast: "font-sans",
            },
          }}
        />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>,
);
